import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import CustomModal from 'components/shared/CustomModal/CustomModal';
import { useEffect, useReducer, useRef, useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';

const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
    },
  },
};

const DealsPriceBookField = ({
  handleInputChange,
  value,
  isEdit,
}: {
  handleInputChange: (field: string, value: any) => void;
  value: any;
  isEdit: boolean;
}) => {
  const [modalState, dispatch] = useReducer(modalReducer, {
    changePriceBook: false,
  });

  const [selectedItems, setSelectedItems] = useState<{
    priceBook: string[];
  }>({
    priceBook: value?.pricebookId || value ? [value?.pricebookId || value] : ['none'],
  });
  const theme = useTheme();
  const [pricebook, setPricebook] = useState<any>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const pricebookId = value?.pricebookId || value;
    if (pricebookId) {
      setSelectedItems({ priceBook: [pricebookId] });
      priceBookRef.current = pricebookId;
    } else {
      setSelectedItems({ priceBook: ['none'] });
      priceBookRef.current = 'none';
    }
  }, [value?.pricebookId, value]);

  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };

  const getPriceBook = async () => {
    try {
      setLoading(true);
      const res = await DealsService.getPricebooks();
      setPricebook(res);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPriceBook();
  }, []);

  const priceBookRef = useRef(value?.pricebookId || value || 'none');

  const handlePriceBookChange = () => {
    if (!priceBookRef.current) return;
    handleSelection(priceBookRef.current, 'priceBook');
    handleInputChange('priceBookId', priceBookRef.current);
    handleInputChange('dealDetails.products', []);
  };

  const handleSelectChange = (e: SelectChangeEvent<string[]>) => {
    const selectedValue = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
    priceBookRef.current = selectedValue;

    if (isEdit) {
      dispatch({ type: 'OPEN', payload: 'changePriceBook' });
    } else {
      handlePriceBookChange();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
      <Typography variant='heading4'>Choose Price Book</Typography>
      <Typography variant='body2' color={zeplinColor.Neutral9}>
        Select a Price Book to associate with this deal. You can add products only from the Price
        Book you associate with this deal. Changing the Price Book deletes all products from the
        deal.
      </Typography>

      <FormControl sx={{ width: '100%' }}>
        <InputLabel
          id='demo-multiple-name-label'
          sx={{
            '&.Mui-focused': { color: zeplinColor.Neutral9 },
          }}
        >
          Price book<span style={{ color: zeplinColor.Error }}></span>
        </InputLabel>
        <Select
          labelId='demo-multiple-name-label'
          id='demo-multiple-name'
          required={true}
          value={selectedItems.priceBook}
          onChange={(e) => {
            handleSelectChange(e);
          }}
          input={
            <OutlinedInput
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  color: zeplinColor.Neutral9,
                  borderColor: zeplinColor.Neutral9,
                },
              }}
              label='Price Book'
            />
          }
          renderValue={(selected) => {
            return loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                }}
              />
            ) : selected?.length ? (
              selected[0] === 'none' ? (
                'None'
              ) : Object.keys(pricebook)?.length &&
                Utilities.getMatchingRecordTypeKey(selectedItems.priceBook[0], pricebook) ? (
                Utilities.getMatchingRecordTypeKey(selectedItems.priceBook[0], pricebook)
              ) : (
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    width: '100%',
                  }}
                />
              )
            ) : (
              ''
            );
          }}
          MenuProps={MenuProps}
          IconComponent={KeyboardArrowDown}
        >
          <MenuItem key={'none'} value={'none'}>
            <Radio
              checked={
                selectedItems?.priceBook?.length
                  ? selectedItems?.priceBook?.indexOf('none') > -1
                  : false
              }
              sx={{
                color: zeplinColor.BasicBlack,
                '&.Mui-checked': {
                  color: zeplinColor.BasicBlack,
                },
              }}
            />
            ----None----
          </MenuItem>
          {Object.keys(pricebook)?.length > 0 &&
            Object.keys(pricebook)?.map((name: any) => (
              <MenuItem key={pricebook[name]} value={pricebook[name]}>
                <Radio
                  checked={
                    selectedItems?.priceBook?.length
                      ? selectedItems?.priceBook?.indexOf(pricebook[name]) > -1
                      : false
                  }
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': {
                      color: zeplinColor.BasicBlack,
                    },
                  }}
                />
                {name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <CustomModal
        handleClose={() => {
          dispatch({ type: 'CLOSE', payload: 'changePriceBook' });
        }}
        open={modalState.changePriceBook}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,
              width: '375px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Confirm Price Book Change
                </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  You have changed the price book for this deal. This action permanently deletes all
                  the products from the deal. Are you sure you want to continue?
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: '16px' }}>
                <Box
                  onClick={() => dispatch({ type: 'CLOSE', payload: 'changePriceBook' })}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={() => {
                    dispatch({ type: 'CLOSE', payload: 'changePriceBook' });
                    handlePriceBookChange();
                  }}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      bgcolor: zeplinColor.Brand1,
                    }}
                    aria-label='button'
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      Yes, Save
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default DealsPriceBookField;
